import * as IF from "infrontjs";
import { AuthState } from "../base/AuthState";
import template from './TokenTemplate.html';

export class TokenState extends AuthState
{
    static ROUTE = '/token/:token';
    static ID = 'token';

    constructor( app, rp )
    {
        super( app, rp );
        this._token = rp.getParam( 'token' );
    }

    canEnter()
    {
        return true;
    }

    async enter()
    {
        const mainWrapper = this.app.container.querySelector( '#main-wrapper' );
        this.app.view.render( mainWrapper, template );

        await this._validateToken( mainWrapper );
    }

    async _validateToken( container )
    {
        try
        {
            const result = await this._resolveTokenWithRegion();

            container.querySelector( '[data-id="token-loading"]' ).style.display = 'none';

            if ( result && result.status === 200 && result.data )
            {
                // Save region from token actionData
                if ( result.data.actionData?.region )
                {
                    this.app.storage.saveRegion( result.data.actionData.region );
                    this.app.api.setRegion( result.data.actionData.region );
                }

                if ( result.data.action === 'validateEmail' || result.data.action === 'confirmEmailChange' )
                {
                    await this._executeEmailConfirmation( container, result.data );
                }
                else if ( result.data.action === 'requestPasswordReset' )
                {
                    this._showPasswordResetForm( container );
                }
                else
                {
                    const message = this._getSuccessMessage( result.data.action );
                    container.querySelector( '[data-id="token-success-message"]' ).textContent = message;
                    container.querySelector( '[data-id="token-success"]' ).style.display = 'block';
                }
            }
            else
            {
                container.querySelector( '[data-id="token-error"]' ).style.display = 'block';
            }
        }
        catch ( error )
        {
            container.querySelector( '[data-id="token-loading"]' ).style.display = 'none';

            if ( error?.status === 404 )
            {
                container.querySelector( '[data-id="token-not-found"]' ).style.display = 'block';
            }
            else if ( error?.status === 410 )
            {
                container.querySelector( '[data-id="token-expired"]' ).style.display = 'block';
            }
            else
            {
                container.querySelector( '[data-id="token-error-message"]' ).textContent = error?.message || 'An unexpected error occurred.';
                container.querySelector( '[data-id="token-error"]' ).style.display = 'block';
            }
        }
    }

    async _resolveTokenWithRegion()
    {
        const regions = [ 'eu', 'us' ];
        const savedRegion = this.app.storage.loadRegion();

        // Try saved region first, then the other
        if ( savedRegion )
        {
            const idx = regions.indexOf( savedRegion );
            if ( idx > 0 )
            {
                regions.splice( idx, 1 );
                regions.unshift( savedRegion );
            }
        }

        let lastError = null;
        for ( const region of regions )
        {
            this.app.api.setRegion( region );
            try
            {
                const { result } = await this.app.api.get( '/auth/token/' + this._token );
                if ( result && result.status === 200 )
                {
                    return result;
                }
            }
            catch ( error )
            {
                lastError = error;
                // If not a 404, don't try other regions
                if ( error?.status !== 404 )
                {
                    throw error;
                }
            }
        }

        // All regions returned 404
        throw lastError;
    }

    async _executeEmailConfirmation( container, tokenData )
    {
        const isEmailChange = tokenData.action === 'confirmEmailChange';
        try
        {
            const { result } = await this.app.api.put( '/auth/token/' + this._token );

            if ( result && result.status === 200 )
            {
                container.querySelector( '[data-id="token-success-title"]' ).textContent =
                    isEmailChange ? 'Email Changed' : 'Email Verified';
                const newEmail = tokenData.actionData?.newEmail || 'your new email';
                container.querySelector( '[data-id="token-success-message"]' ).textContent = isEmailChange
                    ? `Your email has been successfully changed to ${newEmail}. Please use your updated email to log in.`
                    : 'Your email address has been verified. You can now log in and create your first document.';
                container.querySelector( '[data-id="token-success"]' ).style.display = 'block';
            }
            else
            {
                container.querySelector( '[data-id="token-error"]' ).style.display = 'block';
            }
        }
        catch ( error )
        {
            if ( error?.status === 410 )
            {
                container.querySelector( '[data-id="token-expired"]' ).style.display = 'block';
            }
            else
            {
                container.querySelector( '[data-id="token-error-message"]' ).textContent = error?.message ||
                    ( isEmailChange ? 'Failed to confirm email change.' : 'Failed to verify your email. Please try again.' );
                container.querySelector( '[data-id="token-error"]' ).style.display = 'block';
            }
        }
    }

    _showPasswordResetForm( container )
    {
        container.querySelector( '[data-id="token-password-reset"]' ).style.display = 'block';

        this.addEventListener(
            container.querySelector( '[data-id="btn-password-reset"]' ),
            'click',
            async ( e ) => {
                container.querySelector( '[data-id="password-reset-error-container"]' ).style.display = 'none';
                const form = container.querySelector( '[data-id="form-password-reset"]' );
                const formData = IF.Helper.serializeForm( form );

                // Check passwords match
                const confirmInput = container.querySelector( '#resetPasswordConfirm' );
                if ( formData.password !== formData.passwordConfirm )
                {
                    confirmInput.setCustomValidity( 'Passwords do not match.' );
                }
                else
                {
                    confirmInput.setCustomValidity( '' );
                }

                if ( true === form.checkValidity() )
                {
                    container.querySelector( '[data-id="btn-password-reset"]' ).style.display = 'none';
                    container.querySelector( '[data-id="btn-password-reset-loading"]' ).style.display = 'block';
                    try
                    {
                        await this.app.api.put(
                            '/auth/token/' + this._token,
                            {
                                password: formData.password
                            }
                        );

                        container.querySelector( '[data-id="token-password-reset"]' ).style.display = 'none';
                        container.querySelector( '[data-id="token-password-reset-success"]' ).style.display = 'block';
                    }
                    catch( e )
                    {
                        container.querySelector( '[data-id="password-reset-error-container"]' ).innerText = 'Something went wrong. Please try again later.';
                        container.querySelector( '[data-id="password-reset-error-container"]' ).style.display = 'block';
                    }
                    container.querySelector( '[data-id="btn-password-reset-loading"]' ).style.display = 'none';
                    container.querySelector( '[data-id="btn-password-reset"]' ).style.display = 'block';
                }
                else
                {
                    form.classList.add( 'was-validated' );
                }
            }
        );
    }

    _getSuccessMessage( action )
    {
        switch ( action )
        {
            case 'confirmEmailChange':
                return 'Your email change request has been confirmed.';

            case 'validateEmail':
                return 'Your email address has been verified.';

            case 'requestPasswordReset':
                return 'Your token is valid. You can now reset your password.';

            default:
                return 'The token is valid.';
        }
    }
}
